import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Routes, Route, NavLink, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatCAD } from "@/lib/format";
import { LayoutDashboard, Store, Package, Users, ShoppingBag, BarChart3, Tag, Globe, FolderTree } from "lucide-react";

export default function AdminPanel() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) nav("/login");
  }, [user, loading, nav]);
  if (!user) return null;

  return (
    <div className="min-h-screen flex bg-[#1A1A18] text-[#FDFBF7]" data-testid="admin-panel">
      <aside className="w-60 border-r border-[#2a2a26] hidden md:flex flex-col">
        <Link to="/" className="px-5 py-4 border-b border-[#2a2a26]">
          <div className="font-heading text-xl">MapleMarket</div>
          <div className="overline text-[#8A8782]">Control center</div>
        </Link>
        <nav className="flex-1 px-3 py-4 space-y-0.5 text-sm">
          <ANav to="/admin" icon={LayoutDashboard} label="Overview" end />
          <ANav to="/admin/merchants" icon={Store} label="Merchants" />
          <ANav to="/admin/orders" icon={ShoppingBag} label="Orders" />
          <ANav to="/admin/users" icon={Users} label="Users" />
          <ANav to="/admin/billing" icon={Tag} label="Billing rules" />
          <ANav to="/admin/categories" icon={FolderTree} label="Categories" />
          <ANav to="/admin/analytics" icon={BarChart3} label="Analytics" />
          <ANav to="/admin/domains" icon={Globe} label="Custom domains" />
        </nav>
      </aside>
      <main className="flex-1 overflow-auto bg-[#F3F2F0] text-[#1A1A18]">
        <Routes>
          <Route index element={<AdminOverview />} />
          <Route path="merchants" element={<MerchantsPanel />} />
          <Route path="orders" element={<OrdersPanel />} />
          <Route path="users" element={<UsersPanel />} />
          <Route path="billing" element={<BillingPanel />} />
          <Route path="categories" element={<Placeholder title="Categories" />} />
          <Route path="analytics" element={<Placeholder title="Analytics" />} />
          <Route path="domains" element={<Placeholder title="Custom domains" subtitle="Mapping coming soon — database is prepared." />} />
        </Routes>
      </main>
    </div>
  );
}

function ANav({ to, icon: Icon, label, end }) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) =>
      `flex items-center gap-2 px-3 py-2 rounded-sm ${isActive ? "bg-[#702A32] text-white" : "text-[#a8a6a1] hover:bg-[#2a2a26] hover:text-white"}`
    } data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <Icon className="w-4 h-4" strokeWidth={1.5} /> {label}
    </NavLink>
  );
}

function AdminOverview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)); }, []);
  return (
    <div className="p-6 md:p-10" data-testid="admin-overview">
      <div className="overline mb-2">Control center</div>
      <h1 className="font-heading text-4xl">Platform overview</h1>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
        <Tile label="Revenue (paid)" value={formatCAD(stats?.revenue || 0)} testid="admin-revenue" />
        <Tile label="Orders" value={stats?.orders || 0} />
        <Tile label="Merchants" value={stats?.merchants || 0} />
        <Tile label="Products" value={stats?.products || 0} />
        <Tile label="Users" value={stats?.users || 0} />
        <Tile label="Pending merchants" value={stats?.pending_merchants || 0} />
      </div>
    </div>
  );
}
function Tile({ label, value, testid }) {
  return <div className="surface p-5" data-testid={testid}><div className="overline">{label}</div><div className="font-heading text-3xl mt-2">{value}</div></div>;
}

function MerchantsPanel() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/merchants").then(({ data }) => setItems(data)); }, []);
  return (
    <div className="p-6 md:p-10" data-testid="admin-merchants">
      <h1 className="font-heading text-3xl mb-6">Merchants</h1>
      <div className="surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-xs uppercase tracking-wider text-[#5C5A56] text-left">
            <th className="p-4">Name</th><th className="p-4">Slug</th><th className="p-4">Types</th><th className="p-4">Billing</th><th className="p-4">Approved</th>
          </tr></thead>
          <tbody>
            {items.map((m) => (
              <tr key={m.merchant_id} className="border-t border-[#E5E3DD]">
                <td className="p-4"><Link to={`/store/${m.slug}`} className="hover:text-[#702A32]">{m.name}</Link></td>
                <td className="p-4 font-mono text-xs">/store/{m.slug}</td>
                <td className="p-4 text-xs">{m.types.join(", ")}</td>
                <td className="p-4">{m.billing_model} {m.billing_model === "COMMISSION" && `(${m.commission_pct}%)`}</td>
                <td className="p-4">{m.approved ? <span className="tag-pill bg-[#E5EBE8] text-[#2A4B3A]">Approved</span> : <span className="tag-pill bg-[#FBE9E9] text-[#B73B3B]">Pending</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OrdersPanel() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/orders").then(({ data }) => setItems(data)); }, []);
  return (
    <div className="p-6 md:p-10" data-testid="admin-orders">
      <h1 className="font-heading text-3xl mb-6">Orders</h1>
      <div className="surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-xs uppercase tracking-wider text-[#5C5A56] text-left">
            <th className="p-4">Order</th><th className="p-4">Email</th><th className="p-4">Status</th><th className="p-4">Total</th><th className="p-4">Date</th>
          </tr></thead>
          <tbody>
            {items.slice(0, 100).map((o) => (
              <tr key={o.order_id} className="border-t border-[#E5E3DD]">
                <td className="p-4 font-mono text-xs">{o.order_id.slice(-10)}</td>
                <td className="p-4">{o.customer_email}</td>
                <td className="p-4">{o.status}</td>
                <td className="p-4 font-mono">{formatCAD(o.total)}</td>
                <td className="p-4">{new Date(o.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UsersPanel() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/users").then(({ data }) => setItems(data)); }, []);
  return (
    <div className="p-6 md:p-10" data-testid="admin-users">
      <h1 className="font-heading text-3xl mb-6">Users</h1>
      <div className="surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-xs uppercase tracking-wider text-[#5C5A56] text-left">
            <th className="p-4">Name</th><th className="p-4">Email</th><th className="p-4">Role</th><th className="p-4">Joined</th>
          </tr></thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.user_id} className="border-t border-[#E5E3DD]">
                <td className="p-4">{u.name}</td>
                <td className="p-4">{u.email}</td>
                <td className="p-4"><span className="tag-pill bg-[#EFECE6] text-[#5C5A56]">{u.role}</span></td>
                <td className="p-4">{u.created_at?.slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BillingPanel() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/merchants").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const update = async (merchant_id, payload) => {
    await api.patch(`/admin/merchants/${merchant_id}`, payload);
    load();
  };
  return (
    <div className="p-6 md:p-10" data-testid="admin-billing">
      <h1 className="font-heading text-3xl mb-6">Per-merchant billing rules</h1>
      <div className="space-y-3">
        {items.map((m) => (
          <div key={m.merchant_id} className="surface p-5 grid md:grid-cols-5 gap-3 items-center">
            <div className="md:col-span-2">
              <div className="font-medium">{m.name}</div>
              <div className="text-xs text-[#8A8782] font-mono">/store/{m.slug}</div>
            </div>
            <select className="input-field" value={m.billing_model} onChange={(e) => update(m.merchant_id, { billing_model: e.target.value })} data-testid={`billing-model-${m.slug}`}>
              <option value="COMMISSION">Commission</option>
              <option value="SUBSCRIPTION">Subscription</option>
              <option value="MARKUP">Markup</option>
              <option value="HYBRID">Hybrid</option>
            </select>
            <input className="input-field" type="number" step="0.5" value={m.commission_pct} onChange={(e) => update(m.merchant_id, { commission_pct: parseFloat(e.target.value) })} placeholder="Commission %" />
            <input className="input-field" type="number" step="1" value={m.subscription_fee} onChange={(e) => update(m.merchant_id, { subscription_fee: parseFloat(e.target.value) })} placeholder="Sub fee" />
          </div>
        ))}
      </div>
    </div>
  );
}

function Placeholder({ title, subtitle }) {
  return (
    <div className="p-6 md:p-10">
      <h1 className="font-heading text-3xl">{title}</h1>
      {subtitle && <p className="text-[#5C5A56] mt-2">{subtitle}</p>}
      <div className="surface p-12 mt-8 text-center text-[#8A8782]">Scaffolded — full functionality on the roadmap.</div>
    </div>
  );
}
